import { useQuery } from "react-query";

import { AppProperties, getAppProperties } from "@/api";
import {
  DEFAULT_COMMENTS_LABEL,
  DEFAULT_FAVOURITES_LABEL,
  DEFAULT_RESOURCES_LABEL,
  DEFAULT_UP_NEXT_LABEL,
  DEFAULT_WATCHLIST_LABEL,
  TEMPLATE_CALENDAR,
  TEMPLATE_PLAYLISTS,
} from "@/const.ts";
import { useMainTabs } from "@/hooks/useMainTabs.tsx";
import { COLOR_SCHEME } from "@/theme";
import { getUseQueryLocalStorage, setUseQueryLocalStorage } from "@/utils";

import { ExternalLink, useAppPropertyFiles } from "./useAppPropertyFiles";

const key = "appproperties";

export const useAppProperties = (props?: {
  refetchOnMount: "always" | boolean;
}) => {
  const { refetchOnMount } = props ?? {};
  const { data, ...query } = useQuery<AppProperties>(
    [key],
    () => {
      return getAppProperties();
    },
    {
      onSuccess: (data) => {
        setUseQueryLocalStorage(key, data);
      },
      initialData: () => {
        return (getUseQueryLocalStorage(key) as AppProperties) ?? undefined;
      },
      refetchOnMount,
    },
  );

  const { data: files } = useAppPropertyFiles(data, props);

  const { mainTabs } = useMainTabs();

  const isDarkMode = data?.WebColorScheme === "Dark"; // Property isn't used by any app yet, but support is here for future use
  const colorSchemeColors = isDarkMode ? COLOR_SCHEME.DARK : COLOR_SCHEME.LIGHT;

  const externalLinks = files?.WebExternalLinks?.links;

  const rolloutCommunity = data?.RolloutWebCommunity === "1"; // Temporary flag while in development

  const communityEnabled =
    !!data?.AmityClientKey && !!data?.AmityRegion && !!rolloutCommunity;

  return {
    ...query,
    data,
    files,
    watchlistLabel: data?.CustomWatchlistLabel ?? DEFAULT_WATCHLIST_LABEL,
    favouritesLabel: data?.CustomFavoritesLabel ?? DEFAULT_FAVOURITES_LABEL,
    resourcesLabel: data?.CustomVideoResourcesLabel || DEFAULT_RESOURCES_LABEL,
    commentsLabel: data?.CustomVideoCommentsLabel || DEFAULT_COMMENTS_LABEL,
    upNextLabel: data?.CustomVideoUpNextLabel || DEFAULT_UP_NEXT_LABEL,
    showFavourites: data?.HideSidebarFavourites !== "1",
    showWatchlist: data?.HideSidebarWatchlist !== "1",
    showCustomPlaylists: !!mainTabs?.find(
      (t) => t.TemplateId === TEMPLATE_PLAYLISTS,
    ),
    showCommentsOnItems: data?.ShowCommentsForItems === "1",
    showCommentsOnLiveStreams: data?.ShowCommentsForLiveStreams === "1",
    showCalendar: !!mainTabs?.find((t) => t.TemplateId === TEMPLATE_CALENDAR),
    completedMessage:
      data?.CustomWatchedVideoAlertText ?? "Done! Congratulations!",
    rolloutEditProfile: data?.RolloutEditProfile === "1",
    subtitleDisabled: data?.HideSubtitlesButton === "1",
    redirectPurchaseSuccessURL: data?.WebPurchaseCompletedURL,
    isDarkMode,
    colorSchemeColors,
    oldProductNames: data?.OldProductNames,
    oldPlatformManagement: data?.OldPlatformManagement,
    communityEnabled,
    communityGroupsEnabled: data?.CommunityGroupsEnabled === "1",
    communitySearchEnabled: data?.ShowCommunitySearch === "1",
    notificationsEnabled: data?.WebNotificationsEnabled === "1", // Temporary flag while in development
    rolloutCommunity,
    externalCancelURL: data?.ExternalCancelURL ?? "",
    externalManageURL: data?.ExternalManageURL ?? "",
    // appPropertyFiles with types
    externalLinks: externalLinks ? (externalLinks as ExternalLink[]) : [],
  };
};
